import { isOwnerGroupRole, UserRole } from '@/utils/roles';

/** Only the owner holds these; a co-owner or an acting captain never hands them out. */
export const OWNER_ONLY_PERMISSIONS: ReadonlySet<string> = new Set([
  'DeleteVessel',
  'TransferVessel',
]);

/**
 * Why a permission is out of the viewer's reach to hand out:
 * - `owner-only` — only the owner holds it;
 * - `owner-group` — only the owner and co-owners hand it out;
 * - `not-held` — below the owner group you hand out only what you hold yourself.
 */
export type GrantLock = 'owner-only' | 'owner-group' | 'not-held';

export interface Granter {
  /** The raw role string on the service; custom roles included. */
  myRole: string;
  myRights: ReadonlySet<string>;
  /** A captain running a vessel with no owner. */
  actingCaptain: boolean;
}

/** Below the owner group (and a service provider on its own service) you hand out only what you hold. */
export const grantsOnlyHeld = (myRole: string): boolean =>
  !isOwnerGroupRole(myRole) && myRole !== UserRole.SERVICE_PROVIDER;

/**
 * Mirrors the backend `PermissionWithin`: below the owner group you hand out only what you hold,
 * and never ManagePermissions; a co-owner hands out anything but the owner's own. The permissions
 * page locks a row for these, the temporary-hire wizard leaves it out.
 */
export const grantLock = (
  name: string,
  { myRole, myRights, actingCaptain }: Granter,
): GrantLock | undefined => {
  if (OWNER_ONLY_PERMISSIONS.has(name) && (myRole === UserRole.CO_OWNER || actingCaptain)) {
    return 'owner-only';
  }
  if (!grantsOnlyHeld(myRole)) return undefined;
  if (name === 'ManagePermissions' && !actingCaptain) return 'owner-group';
  return myRights.has(name) ? undefined : 'not-held';
};

export const canGrant = (name: string, granter: Granter): boolean => !grantLock(name, granter);
